import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';
import { useMemo } from 'react';

import { ConfirmActionDialog } from '@/components/Dialog/ConfirmActionDialog';
import { HStack, VStack } from '@/components/Layout';
import StepperNavigation from '@/components/Navigation/StepperNavigation';
import AddStepperStepTitleCard from '@/components/Stepper/AddStepperStepTitleCard';
import DockAddStepperAction from '@/components/Stepper/DockAddStepperAction';
import { toast } from '@/components/ui/use-toast';
import { handleApiError } from '@/lib/api/error';
import { defaultNS, useTranslation } from '@/lib/i18n';

import {
  Step1Edit,
  Step2Name,
  Step3Info,
  Step4PurposeRole,
  Step5Structure,
  Step6People,
} from '../../-components/steps';
import { useOrgUnitForm } from '../../-orgUnitWizard/hooks/useOrgUnitForm';
import { useUnitDetailPrefill } from '../../-orgUnitWizard/hooks/useUnitDetailPrefill';
import { useWizardNavigator } from '../../-orgUnitWizard/hooks/useWizardNavigator';
import type { EditUnitContext } from '../../-orgUnitWizard/machine/edit-unit.machine';
import type { StepHash } from '../../-orgUnitWizard/machine/unitTypes';
import { buildEditUnitCommand } from '../../-orgUnitWizard/transforms';
import { useRestructuringUnitOptions } from '../../-orgUnitWizard/view-model';
import { useDepartmentRestructuringSkeletonQuery } from '../../-query';
import { useDepartmentUnitDetailQuery } from '../../-query/departmentUnit';

import type {
  DeleteDepartmentUnitRequest,
  PutDepartmentUnitRequest,
} from './-query';
import { useDeleteDepartmentUnit, usePutDepartmentUnit } from './-query';

export const Route = createFileRoute(
  '/_staff/organization/plan/$id/edit/$unitId/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t, i18n } = useTranslation(defaultNS, {
    keyPrefix: 'pages.organization.plan',
  });

  const STEP_LABELS = [
    t('addOrEditOrgUnit.step1UpdateProcess.title'),
    t('addOrEditOrgUnit.step2OrgUnitName.title'),
    t('addOrEditOrgUnit.step3OrgUnitInfo.title'),
    t('addOrEditOrgUnit.step4PurposeRole.title'),
    t('addOrEditOrgUnit.step5Structure.title'),
    t('addOrEditOrgUnit.step6People.title'),
  ] as const;

  const { id, unitId } = useParams({
    from: '/_staff/organization/plan/$id/edit/$unitId/',
  });
  const navigate = useNavigate();

  const {
    state,
    send,
    setName,
    setShortName,
    setUnitId,
    setOffice,
    setBusinessCardName,
    setDescription,
    setFunctionalRole,
    setMission,
    setHierarchyLevel,
    setOrderInHierarchy,
    setParentUnitId,
    setChildUnitIds,
    setPersons,
    setResponsible,
    setSubResponsible,
    setAction,
    setConfirmOpen,
  } = useOrgUnitForm('edit');

  const { locationHash, previousPage, goto } = useWizardNavigator(
    state.context.step,
    send,
  );

  const { action, confirmOpen } = state.context as EditUnitContext;

  const { data: unitDetail } = useDepartmentUnitDetailQuery(id, unitId);
  const { data: planDetail } = useDepartmentRestructuringSkeletonQuery(id);
  const isEditable = planDetail?.status === 'InProgress';
  const { flatOptions, childOptions } = useRestructuringUnitOptions(
    id,
    state.context.parentUnitId,
  );

  // ユニット詳細取得後に 1 度だけプリフィルを実行
  useUnitDetailPrefill({
    unitDetail: unitDetail,
    setters: {
      setName,
      setShortName,
      setUnitId,
      setDescription,
      setFunctionalRole,
      setMission,
      setHierarchyLevel,
      setOrderInHierarchy,
      setParentUnitId,
      setChildUnitIds,
      setOffice,
      setBusinessCardName,
      setPersons,
      setResponsible,
      setSubResponsible,
    },
  });

  const nextPage = useMemo<StepHash | null>(() => {
    const n = Number(locationHash);
    if (n >= STEP_LABELS.length) return null;
    if (locationHash === '1' && action === 'abolish') return null;
    return (n + 1).toString() as StepHash;
  }, [locationHash, action, STEP_LABELS.length]);

  const { mutateAsync: updateUnit, isPending: isUpdating } =
    usePutDepartmentUnit(id, unitId);
  const { mutateAsync: deleteUnit, isPending: isDeleting } =
    useDeleteDepartmentUnit(id, unitId);

  const disabled =
    !state.can({ type: 'NEXT' }) || isUpdating || isDeleting || !isEditable;

  const handleNext = () => {
    if (locationHash === '1' && action === 'abolish') {
      setConfirmOpen(true);
      return;
    }

    if (nextPage) {
      goto(nextPage);
    }
  };

  const buildUpdatePayload = (): PutDepartmentUnitRequest => {
    if (!planDetail) throw new Error('plan detail not loaded');
    return buildEditUnitCommand(state.context, planDetail.version);
  };

  const handleSave = async () => {
    if (!isEditable) return;
    try {
      await updateUnit(buildUpdatePayload());
      navigate({ to: `/organization/plan/${id}` });
    } catch (e) {
      const { message } = handleApiError(e, { language: i18n.language });
      toast({ description: message, variant: 'destructive' });
    }
  };

  const handleAbolish = async () => {
    if (!planDetail || !isEditable) return;
    const body: DeleteDepartmentUnitRequest = {
      version: planDetail.version,
    } as DeleteDepartmentUnitRequest;

    try {
      await deleteUnit(body);
      navigate({ to: `/organization/plan/${id}` });
    } catch (e) {
      const { message } = handleApiError(e, { language: i18n.language });
      toast({ description: message, variant: 'destructive' });
    }
  };

  const sections = STEP_LABELS.map((label, index) => ({
    label,
    hash: (index + 1).toString(),
  }));

  return (
    <>
      <HStack className="h-[calc(100vh-64px)] w-full items-start gap-9 p-10">
        <StepperNavigation
          link={{
            hash: locationHash,
            to: `/organization/plan/${id}/edit/${unitId}`,
          }}
          title={t('addOrEditOrgUnit.editTitle') as string}
          sections={sections}
        />

        <VStack className="h-full min-w-0 flex-1 items-start gap-6">
          <AddStepperStepTitleCard
            stepName={`${locationHash}. ${STEP_LABELS[Number(locationHash) - 1]}`}
            className="w-full"
          />

          {locationHash === '1' && (
            <Step1Edit action={action} setAction={setAction} />
          )}

          {locationHash === '2' && (
            <Step2Name
              name={state.context.name}
              shortName={state.context.shortName}
              businessCardName={state.context.businessCardName || ''}
              setName={setName}
              setShortName={setShortName}
              setBusinessCardName={setBusinessCardName}
            />
          )}

          {locationHash === '3' && (
            <Step3Info
              unitId={state.context.unitId || ''}
              office={state.context.office || ''}
              setUnitId={setUnitId || (() => {})}
              setOffice={setOffice || (() => {})}
            />
          )}

          {locationHash === '4' && (
            <Step4PurposeRole
              description={state.context.description}
              functionalRole={state.context.functionalRole}
              mission={state.context.mission}
              setDescription={setDescription}
              setFunctionalRole={setFunctionalRole}
              setMission={setMission}
            />
          )}

          {locationHash === '5' && (
            <Step5Structure
              hierarchyLevel={state.context.hierarchyLevel}
              setHierarchyLevel={setHierarchyLevel}
              orderInHierarchy={state.context.orderInHierarchy}
              setOrderInHierarchy={setOrderInHierarchy}
              parentUnitId={state.context.parentUnitId}
              setParentUnitId={setParentUnitId}
              childUnitIds={state.context.childUnitIds}
              setChildUnitIds={setChildUnitIds}
              flatOptions={flatOptions}
              childOptions={childOptions}
            />
          )}

          {locationHash === '6' && (
            <Step6People
              planId={id}
              responsible={state.context.responsible ?? null}
              subResponsible={state.context.subResponsible ?? null}
              persons={state.context.persons}
              onResponsibleChange={(p) =>
                (setResponsible || (() => {}))(p ?? undefined)
              }
              onSubResponsibleChange={(p) =>
                (setSubResponsible || (() => {}))(p ?? undefined)
              }
              onPersonsChange={setPersons}
            />
          )}

          {/* Action dock */}
          <div className="mb-32 w-full">
            <DockAddStepperAction
              baseLink={`/organization/plan/${id}/edit/${unitId}`}
              previousPage={previousPage}
              nextPage={nextPage}
              nextComplete={!nextPage}
              disabled={disabled}
              onNextClick={handleNext}
              onCompleteClick={
                locationHash === '1' && action === 'abolish'
                  ? () => setConfirmOpen(true)
                  : handleSave
              }
            />
          </div>
        </VStack>
      </HStack>

      {/* confirm dialog */}
      <ConfirmActionDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleAbolish}
        title={{
          action: t('addOrEditOrgUnit.step1UpdateProcess.abolish'),
          resource: t('addOrEditOrgUnit.editTitle'),
        }}
        description={t(
          'addOrEditOrgUnit.step1UpdateProcess.abolishDescription',
        )}
        okVariant="destructive"
      />
    </>
  );
}
